/**
 * AP-39 10.2, 10.5 — Abschnitt **„Antwort des Lieferanten"** an einer Preisanfrage: Kopf (Antwort vom,
 * Angebotsnummer, gültig bis) und je Belegzeile die Preisfelder. Erfassbar auch an einem
 * festgeschriebenen und gesendeten Beleg — die Antwort ist nicht sein Inhalt.
 *
 * AP-39 11. — derselbe Abschnitt als **„Bestätigung des Lieferanten"** an einer Bestellung: je Zeile
 * bestätigte Menge, Preis und Liefertermin, „wie bestellt" mit einem Klick, daneben der Abgleich gegen die
 * Bestellung (Menge, Preis, Termin) und im Kopf der Stand (bestätigt · mit Abweichung · teilweise).
 *
 * AP-39 Nachtrag 2 — die Zeilen stehen im **Raster der Positionen** (`SupplierResponseGrid`): dieselben
 * Spalten, grün, was gilt, violett darunter, was die KI gelesen hat. An einer Preisanfrage „Als Bestellung
 * übernehmen" — derselbe Weg wie „Umwandeln in → Bestellung", der die Preise des Angebots mitnimmt.
 *
 * Gespeichert wird **feldweise ohne Knopf** (AP-36): eine Speicherstelle für den Kopf, eine je Zeile.
 * Gelesene Werte (KI) stehen als Vorschlag unter der Zeile, bis jemand bestätigt (2.2 Satz 4).
 * Kalkulation und Einkauf im Projekt lesen dieselbe Antwort — hier wird sie nur einmal erfasst.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, ChevronRight, History, Loader2, ShoppingCart, Sparkles } from 'lucide-react'
import { geldText, preisText, type DocumentResponseKind, type DocumentResponseView } from '@symphenia/shared-types'
import { kiFarbe } from '@/components/ki-vorschlag/KiVorschlag'
import { apiErrorText } from '@/lib/api-error'
import { bestaetige } from '@/lib/dialoge'
import { fmtDate } from '@/lib/format-date'
import { FeldStatusZeichen, feldKlasse, useFeld, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { responsesApi, type EarlierResponse } from '../api/documents.api'
import type { UnitRef } from '../types/document.types'
import { SupplierResponseGrid, useTexte } from './SupplierResponseGrid'

const STAND_FARBE: Record<string, string> = {
  CONFIRMED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  CONFIRMED_DEVIATION: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  PARTIAL: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
}

/**
 * Ein Kopf ohne Antwort. **Eine Konstante, kein Literal im Render:** Die Speicherstelle übernimmt einen
 * Datensatz ohne Stand bei jeder neuen Referenz — ein neues Objekt je Render wäre eine Schleife.
 */
const KOPF_LEER = { externalNumber: null, externalDate: null, validUntil: null, note: null }

export function SupplierResponseBlock({
  documentId,
  kind,
  darfSchreiben,
  preise,
  einheiten,
  neuLaden,
  bestellen,
}: {
  documentId: string
  kind: DocumentResponseKind
  /** Bearbeitungsrecht der Belegart — auch am festgeschriebenen Beleg */
  darfSchreiben: boolean
  /** `correspondence.price.view` — ohne es bleiben Preise verborgen und nichts ist erfassbar */
  preise: boolean
  einheiten: UnitRef[]
  /** hochgezählt, wenn die Antwort von außen neu ist (die KI hat im Posteingang gelesen) — lädt neu und klappt auf */
  neuLaden?: number
  /**
   * AP-39 Nachtrag 2 (5.): „Als Bestellung übernehmen" — nur an einer Preisanfrage und nur, wer Bestellungen anlegen
   * darf. `markiert`: so viele Positionen sind im Raster markiert; dann kommen nur sie mit.
   */
  bestellen?: { markiert: number; onClick: () => void; laeuft: boolean }
}) {
  const t = useTexte(kind)
  const bestaetigung = kind === 'CONFIRMATION'
  const [daten, setDaten] = useState<DocumentResponseView | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [offen, setOffen] = useState<boolean | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [haken, setHaken] = useState<Set<string>>(new Set())
  const [kopfHaken, setKopfHaken] = useState(true)
  const [frueher, setFrueher] = useState<EarlierResponse | null>(null)
  const zeit = useRef<ReturnType<typeof setTimeout>>()

  const laden = useCallback(async () => {
    try {
      const d = await responsesApi.get(documentId, kind)
      setDaten(d)
      setFehler(null)
      // Zugeklappt, solange es nichts zu sehen gibt — eine Preisanfrage ohne Antwort soll nicht länger werden.
      setOffen((alt) => alt ?? (d.current !== null || d.extra.length > 0))
    } catch (e) {
      setFehler(apiErrorText(e, t('loadFailed')))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `t` ist je Render neu; die Meldung entsteht erst beim Fehler
  }, [documentId, kind])
  useEffect(() => {
    void laden()
    return () => clearTimeout(zeit.current)
  }, [laden])
  useEffect(() => {
    if (neuLaden) void laden().then(() => setOffen(true))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neuLaden])
  /** Nach einem Feld: Summe, Zähler und „Antwort vom" nachziehen — einmal, nicht je Tastendruck. */
  const nachSpeichern = useCallback(() => {
    clearTimeout(zeit.current)
    zeit.current = setTimeout(() => void laden(), 400)
  }, [laden])

  const darf = darfSchreiben && preise && !!daten?.writable
  const vorschlaege = useMemo(
    () => [...(daten?.lines.flatMap((l) => (l.response?.suggested ? [l.response] : [])) ?? []), ...(daten?.extra.filter((z) => z.suggested) ?? [])],
    [daten],
  )
  const vorschlagSchluessel = vorschlaege.map((z) => z.id).join(',')
  useEffect(() => {
    // Vorgabe-Häkchen wie in der Kalkulation: nicht bei „niedrig".
    setHaken(new Set(vorschlaege.filter((z) => z.suggested?.confidence !== 'niedrig').map((z) => z.id)))
    setKopfHaken(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vorschlagSchluessel])

  const kopfStelle = useSpeicherstelle<Datensatz>({
    schluessel: `docResponse:${documentId}:${kind}`,
    bezeichnung: t('title'),
    daten: (daten ? (daten.current ?? KOPF_LEER) : null) as Datensatz | null,
    schreibe: async (patch, stand) => {
      const r = await responsesApi.head(documentId, kind, patch, stand)
      nachSpeichern()
      return (r.data ?? {}) as unknown as Datensatz
    },
    keepalive: { url: responsesApi.url(documentId, kind), method: 'PATCH' },
  }) as Speicherstelle<unknown>
  const antwortVom = useFeld<string | null>(kopfStelle, 'externalDate', { art: 'datum', label: t('date'), gesperrt: !darf })
  const nummer = useFeld<string | null>(kopfStelle, 'externalNumber', { label: t('number'), gesperrt: !darf })
  const gueltig = useFeld<string | null>(kopfStelle, 'validUntil', { art: 'datum', label: t('validUntil'), gesperrt: !darf })
  const bemerkung = useFeld<string | null>(kopfStelle, 'note', { label: t('note'), gesperrt: !darf })

  const aktion = async (was: () => Promise<DocumentResponseView | unknown>) => {
    setLaeuft(true)
    setFehler(null)
    try {
      await was()
      await laden()
    } catch (e) {
      setFehler(apiErrorText(e, t('failed')))
    } finally {
      setLaeuft(false)
    }
  }

  if (!daten) return fehler ? <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div> : null

  const kopfVorschlag = daten.current?.suggested ?? null
  const zusammenfassung = daten.current
    ? [
        t('answered', { answered: daten.linesAnswered, total: daten.linesTotal }),
        daten.current.externalNumber ? `${t('number')} ${daten.current.externalNumber}` : '',
        daten.current.externalDate ? fmtDate(daten.current.externalDate) : '',
        preise && daten.total ? `${t('total')} ${geldText(daten.total)}` : '',
      ]
        .filter(Boolean)
        .join(' · ')
    : t('none')
  // 5.1: Bestellen lohnt, sobald ein Preis gilt oder eine zusätzliche Zeile bestätigt ist (Gelesenes zählt nicht, PE-115).
  const bestellbar =
    !!bestellen &&
    !bestaetigung &&
    (daten.lines.some((l) => l.response?.unitPrice != null && !l.response.notOffered) || daten.extra.some((z) => z.confirmedAt && z.unitPrice != null && !z.notOffered))

  return (
    <section className="rounded-lg border border-border bg-card p-4 shadow-sm" aria-label={t('title')}>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className="flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground" aria-expanded={!!offen} onClick={() => setOffen((x) => !x)}>
          {offen ? <ChevronDown className="h-4 w-4" aria-hidden /> : <ChevronRight className="h-4 w-4" aria-hidden />}
          {t('title')}
        </button>
        <span className="text-sm text-muted-foreground">{zusammenfassung}</span>
        {/* 11.3: Stand der Bestellung — bestätigt · bestätigt mit Abweichung · teilweise bestätigt. */}
        {bestaetigung && daten.state && (
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STAND_FARBE[daten.state] ?? ''}`} title={daten.deviationText ?? undefined}>
            {t(`states.${daten.state}`)}
            {daten.deviationCount ? ` — ${daten.deviationCount === 1 ? daten.deviationText : t('deviations', { count: daten.deviationCount })}` : ''}
          </span>
        )}
        {vorschlaege.length > 0 && <span className="rounded-full bg-violet-500/15 px-2 py-0.5 text-xs font-medium text-violet-700 dark:text-violet-300">{t('suggestions', { count: vorschlaege.length })}</span>}
        {laeuft && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        <span className="ml-auto flex flex-wrap items-center gap-2">
          {offen && darf && bestaetigung && daten.linesAnswered < daten.linesTotal && (
            <button type="button" disabled={laeuft} className="rounded-md border border-border px-2 py-1 text-xs hover:bg-muted disabled:opacity-50" onClick={() => void aktion(() => responsesApi.asOrdered(documentId))}>
              {t('allAsOrdered')}
            </button>
          )}
          {offen && darf && daten.current && daten.linesAnswered > 0 && (
            <button
              type="button"
              className="rounded-md border border-border px-2 py-1 text-xs hover:bg-muted"
              onClick={async () => {
                if (await bestaetige(t('newConfirm'), { ok: t('newAction') })) void aktion(() => responsesApi.startNew(documentId, kind))
              }}
            >
              {t('newAction')}
            </button>
          )}
          {/* AP-39 Nachtrag 2 (5.): dort, wo die Preise stehen — derselbe Weg wie „Umwandeln in → Bestellung" (PE-109). */}
          {bestellbar && (
            <button
              type="button"
              // Die Übernahme nimmt die markierten Positionen mit — ein Klick hier hebt die Markierung nicht auf.
              data-auswahl-behalten
              disabled={bestellen.laeuft}
              title={vorschlaege.length ? `${t('orderActionHint')} ${t('confirmFirst')}` : t('orderActionHint')}
              className="inline-flex items-center gap-1.5 rounded-md border border-emerald-600/50 bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-800 hover:bg-emerald-500/20 disabled:opacity-50 dark:text-emerald-200"
              onClick={bestellen.onClick}
            >
              <ShoppingCart className="h-3.5 w-3.5" aria-hidden />
              {t('orderAction')}
              {bestellen.markiert > 0 && <span className="font-normal">({t('orderSelected', { count: bestellen.markiert })})</span>}
            </button>
          )}
        </span>
      </div>
      {fehler && <div className="mt-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</div>}

      {offen && (
        <div className="mt-3 space-y-3">
          {!preise && <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">{t('noPriceRight')}</p>}
          {preise && !daten.writable && <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">{t('closed')}</p>}

          <div className="grid gap-3 text-sm sm:grid-cols-4">
            <label className="block">
              <span className="mb-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                {t('date')} <FeldStatusZeichen status={antwortVom} />
              </span>
              <input type="date" className={`${feldKlasse} w-full`} {...antwortVom.props} />
            </label>
            <label className="block">
              <span className="mb-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                {t('number')} <FeldStatusZeichen status={nummer} />
              </span>
              <input className={`${feldKlasse} w-full`} {...nummer.props} />
            </label>
            {!bestaetigung && (
              <label className="block">
                <span className="mb-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                  {t('validUntil')} <FeldStatusZeichen status={gueltig} />
                </span>
                <input type="date" className={`${feldKlasse} w-full`} {...gueltig.props} />
              </label>
            )}
            <label className="block">
              <span className="mb-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                {t('note')} <FeldStatusZeichen status={bemerkung} />
              </span>
              <input className={`${feldKlasse} w-full`} {...bemerkung.props} />
            </label>
          </div>

          {/* Gelesen und noch nicht bestätigt — in der Farbe des KI-Vorschlags (AP-39 Nachtrag 1, PE-99). */}
          {preise && (vorschlaege.length > 0 || kopfVorschlag) && (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-violet-500/50 bg-violet-500/5 px-3 py-2 text-xs">
              <span className={`inline-flex items-center gap-1 font-medium ${kiFarbe}`}>
                <Sparkles className="h-3.5 w-3.5" aria-hidden />
                {t('suggestionHint')}
              </span>
              {kopfVorschlag && (
                <label className="flex items-center gap-1 italic">
                  <input type="checkbox" checked={kopfHaken} disabled={!darf} onChange={(e) => setKopfHaken(e.target.checked)} />
                  {[kopfVorschlag.supplierOfferNumber ? `${t('number')} ${kopfVorschlag.supplierOfferNumber}` : '', kopfVorschlag.validUntil ? `${t('validUntil')} ${fmtDate(kopfVorschlag.validUntil)}` : '']
                    .filter(Boolean)
                    .join(' · ') || t('head')}
                  {kopfVorschlag.fileName ? <span className="text-muted-foreground"> — {kopfVorschlag.fileName}</span> : null}
                </label>
              )}
              {darf && (
                <span className="ml-auto flex gap-2">
                  <button
                    type="button"
                    disabled={laeuft || (!haken.size && !(kopfHaken && kopfVorschlag))}
                    className="rounded-md bg-primary px-2 py-1 font-medium text-primary-foreground disabled:opacity-50"
                    onClick={() => void aktion(() => responsesApi.confirm(documentId, kind, [...haken], kopfHaken && !!kopfVorschlag))}
                  >
                    {t('confirm', { count: haken.size })}
                  </button>
                  <button
                    type="button"
                    disabled={laeuft}
                    className="rounded-md border border-border px-2 py-1 hover:bg-muted"
                    onClick={() => void aktion(() => responsesApi.discard(documentId, kind, vorschlaege.map((z) => z.id), true))}
                  >
                    {t('discard')}
                  </button>
                </span>
              )}
            </div>
          )}

          <SupplierResponseGrid
            documentId={documentId}
            kind={kind}
            daten={daten}
            darf={darf}
            preise={preise}
            einheiten={einheiten}
            haken={haken}
            onHaken={(id, an) => setHaken((alt) => umschalten(alt, id, an))}
            onGespeichert={nachSpeichern}
            onZusatzAnlegen={(label) => aktion(() => responsesApi.addExtra(documentId, kind, { label }))}
            onZusatzLoeschen={(id) => void aktion(() => responsesApi.removeExtra(documentId, kind, id))}
            laeuft={laeuft}
          />

          {daten.earlier.length > 0 && (
            <div className="space-y-1 border-t border-border pt-2 text-xs">
              <h3 className="flex items-center gap-1 font-semibold uppercase tracking-wide text-muted-foreground">
                <History className="h-3.5 w-3.5" aria-hidden /> {t('earlierTitle')}
              </h3>
              {daten.earlier.map((a) => (
                <div key={a.id}>
                  <button
                    type="button"
                    className="text-primary hover:underline"
                    aria-expanded={frueher?.id === a.id}
                    onClick={() => (frueher?.id === a.id ? setFrueher(null) : void responsesApi.earlier(documentId, kind, a.id).then(setFrueher).catch((e) => setFehler(apiErrorText(e, t('loadFailed')))))}
                  >
                    {[a.externalNumber ?? t('withoutNumber'), a.externalDate ? fmtDate(a.externalDate) : '', t('linesAnswered', { count: a.linesAnswered })].filter(Boolean).join(' · ')}
                  </button>
                  {frueher?.id === a.id && <FruehereAntwort antwort={frueher} zeilen={daten.lines} preise={preise} />}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  )
}

function umschalten(alt: Set<string>, id: string, an: boolean): Set<string> {
  const n = new Set(alt)
  if (an) n.add(id)
  else n.delete(id)
  return n
}

/** Eine frühere Antwort zum Nachlesen (PE-18) — nur lesend. */
function FruehereAntwort({ antwort, zeilen, preise }: { antwort: EarlierResponse; zeilen: DocumentResponseView['lines']; preise: boolean }) {
  const t = useTexte('OFFER')
  const name = new Map(zeilen.map((l) => [l.lineId, l]))
  return (
    <table className="mt-1 w-full text-xs">
      <tbody>
        {antwort.lines.map((z) => {
          const l = z.lineId ? name.get(z.lineId) : undefined
          return (
            <tr key={z.id} className="border-b border-border/60">
              <td className="py-0.5 pr-2">{l?.name ?? z.label ?? '—'}</td>
              <td className="whitespace-nowrap py-0.5 pr-2 text-right tabular-nums">
                {z.notOffered ? t('notOffered') : preise && z.netPrice != null ? `${preisText(z.netPrice)} ${t('per')} ${z.unitCode ?? l?.unitCode ?? ''}` : ''}
              </td>
              <td className="whitespace-nowrap py-0.5 text-right text-muted-foreground">
                {z.leadTimeDays != null ? t('days', { count: z.leadTimeDays }) : ''}
                {z.deliveryAt ? fmtDate(z.deliveryAt) : ''}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
